import { Component, computed, effect, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Voucher } from 'depoto-core/src/entities'
import { historyBack } from '../../utils'
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms'
import { CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR, FormRowBase } from '../../components/_common/form-row-base/form-row-base'
import { NotificationService } from '../../services'
import { TranslateService } from '@ngx-translate/core'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { updateCachedListQueries } from '../../services/query-cache.service'
import { PayloadBuilderService } from '../../services/payload-builder.service'
import { VoucherService } from '../../services/entity-services/voucher.service'

export const voucherSchema = {
  id: null,
  name: null,
  code: null,
  externalId: null,
  discountType: null,
  discountPercent: null,
  discountValue: null,
  maxUse: null,
  validFrom: null,
  validTo: null,
  isValid: null,
  isPayment: null,
  enabled: null,
  used: null,
}

@Component({
  selector: 'app-voucher',
  templateUrl: './voucher.component.html',
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR(VoucherComponent)],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VoucherComponent extends FormRowBase implements CheckDataChanges, OnDestroy {
  private queryClient = injectQueryClient()
  voucherService = inject(VoucherService)

  // Overridden in the constructor below and restored in ngOnDestroy - see there for why.
  private previousShouldReuseRoute: Router['routeReuseStrategy']['shouldReuseRoute']

  submitted = signal(false)
  private initialFormValue = signal<any>(null)

  discountTypeOptions = [
    { id: 'percentage', name: 'voucher.discountType.percentage' },
    { id: 'value', name: 'voucher.discountType.value' },
  ]

  voucherForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, [Validators.required]),
    code: new FormControl({ value: '', disabled: false }, [Validators.required]),
    discountType: new FormControl({ value: '', disabled: false }, [Validators.required]),
    discountPercent: new FormControl({ value: 0, disabled: false }, []),
    discountValue: new FormControl({ value: 0, disabled: false }, [Validators.pattern('^[0-9]*$')]),
    maxUse: new FormControl({ value: 0, disabled: false }, [Validators.required, Validators.pattern('^[0-9]*$')]),
    validFrom: new FormControl({ value: '', disabled: false }, [Validators.required]),
    validTo: new FormControl({ value: '', disabled: false }, [Validators.required]),
    enabled: new FormControl({ value: false, disabled: false }, []),
    isPayment: new FormControl({ value: false, disabled: false }, []),
    externalId: new FormControl({ value: '', disabled: false }, []),
  })

  readonly queryVoucher = injectQuery(() => ({
    queryKey: ['voucher', Number(this.route.snapshot.params['id'])],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.voucherService.getVoucher(id, voucherSchema)
      } else {
        return new Voucher()
      }
    },
    onSuccess: data => {
      this.fillFormValue(data)
    },
    // enabled: !!this.router.url.split('/')[2],
  }))

  private payloadBuilder = inject(PayloadBuilderService)

  mutationCreateVoucher = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (this.voucherForm.invalid) {
        throw new Error('Form is invalid')
        return
      }
      this.setMinMaxValidation()

      this.convertToServerFormat()
      const n = this.getDataToSave()
      // Return the created voucher so onSuccess receives the created entity (with id)
      return await this.voucherService.createVoucher(n, voucherSchema)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.voucherForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.voucherForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('alert.voucher.created'))

      // Update the detail cache for this voucher so consumers see fresh data immediately
      this.queryClient.setQueryData(['voucher', Number(data['id'])], data)

      // OPTIONS: keep as-is → update options cache so selects/filters see the new voucher immediately
      updateCachedListQueries(this.queryClient, ['vouchers', 'options'], data, {
        mode: 'insert',
        insertPosition: 'start',
      })

      // LISTING ['vouchers', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removeVoucherListings()

      // Optionally keep invalidation for full listing pages
      this.router.navigate(['/voucher', data['id']], { replaceUrl: true })
    },
  }))

  mutationUpdateVoucher = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      // this.getDirtyValues() // todo ???

      this.setMinMaxValidation()
      this.convertToServerFormat()
      const n = this.getDataToSave()

      return await this.voucherService.updateVoucher(n, voucherSchema)
    },
    onSuccess: data => {
      this.submitted.set(false)
      this.convertToDatetimeInputFormat()
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.voucherForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.voucherForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('alert.voucher.saved'))

      // Update the detail cache for this voucher with returned server data
      this.queryClient.setQueryData(['voucher', data['id']], data)

      // OPTIONS: keep as-is → update options cache value for this voucher
      updateCachedListQueries(this.queryClient, ['vouchers', 'options'], data, { mode: 'update' })

      // LISTING ['vouchers', sortProp, sortDir]: On UPDATE → if id is present in cached pages, refetch that exact query; otherwise do nothing
      this.removeVoucherListings()
    },
    enabled: !!this.router.url.split('/')[2],
  }))

  mutationDelete = injectMutation(() => ({
    mutationFn: async () => {
      // this.getDirtyValues()
      const id = Number(this.route.snapshot.params['id'])
      await this.voucherService.deleteVoucher({ id })
    },
    onSuccess: () => {
      this.submitted.set(false)
      this.notificationService.success(this.translateService.instant('alert.voucher.deleted'))
      // Prune deleted customer from any cached lists (supports infinite queries and arrays)
      const idStr = String(this.route.snapshot.params['id'])
      updateCachedListQueries(this.queryClient, ['vouchers'], { id: Number(idStr) } as any, { mode: 'delete' })
      // Remove detail cache for this customer
      this.queryClient.removeQueries({ queryKey: ['voucher', Number(idStr)], exact: true })
      this.router.navigate(['/vouchers'], { replaceUrl: true })
    },
    enabled: !!this.router.url.split('/')[2],
  }))

  isCreating = computed(() => !this.route.snapshot.params['id'])

  loading = computed(() => {
    return this.queryVoucher.isFetching() || this.queryVoucher.isPending()
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {
    effect(() => {
      const data = this.queryVoucher.data()
      if (data) {
        this.fillFormValue(data)
        this.convertToDatetimeInputFormat()
      }
    })
    super()
    // Forces this route to reload fresh (rather than being reused) when navigating between
    // different voucher ids. This mutates the app-wide Router's routeReuseStrategy though, not
    // something scoped to this component - left in place it silently breaks route reuse (e.g.
    // wizard-style child routes elsewhere, like order creation) for the rest of the session. Put
    // the original back on the way out.
    this.previousShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute.bind(this.router.routeReuseStrategy)
    this.router.routeReuseStrategy.shouldReuseRoute = () => false
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.previousShouldReuseRoute
  }

  public fillFormValue(voucher) {
    const value = {
      name: voucher.name || '',
      code: voucher.code || '',
      discountType: voucher.discountType || '',
      discountPercent: voucher.discountPercent || 0,
      discountValue: voucher.discountValue || 0,
      maxUse: voucher.maxUse || 0,
      validFrom: voucher.validFrom || '',
      validTo: voucher.validTo || '',
      enabled: voucher.enabled || false,
      isPayment: voucher.isPayment || false,
      externalId: voucher.externalId || '',
    }

    this.voucherForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone(value))
    this.voucherForm.markAsPristine()
  }

  setMinMaxValidation() {
    if (this.voucherForm.value.discountType === 'percent') {
      this.voucherForm.controls['discountPercent'].setValidators([Validators.max(100), Validators.min(1)])
    } else {
      this.voucherForm.controls['discountPercent'].clearValidators()
    }
  }

  hasUnsavedDataChanges(): boolean {
    // todo
    return false
  }

  goBack(): void {
    historyBack()
  }

  private convertToDatetimeInputFormat() {
    if (this.voucherForm.value.validFrom && !this.voucherForm.value.validFrom.includes('T')) {
      const validFrom = this.voucherForm.value.validFrom.replace(' ', 'T')
      this.voucherForm.get('validFrom')?.setValue(validFrom)
    }
    if (this.voucherForm.value.validTo && !this.voucherForm.value.validTo.includes('T')) {
      const validTo = this.voucherForm.value.validTo.replace(' ', 'T')
      this.voucherForm.get('validTo')?.setValue(validTo)
    }
  }

  private convertToServerFormat() {
    if (this.voucherForm.value.validFrom !== null && this.voucherForm.value.validFrom.includes('T')) {
      const validFrom = this.voucherForm.value.validFrom.replace('T', ' ')
      this.voucherForm.get('validFrom')?.setValue(validFrom)
    }
    if (this.voucherForm.value.validTo !== null && this.voucherForm.value.validTo.includes('T')) {
      const validTo = this.voucherForm.value.validTo.replace('T', ' ')
      this.voucherForm.get('validTo')?.setValue(validTo)
    }
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.voucherForm.controls
  }

  minMaxValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value > 100 || control.value < 0) {
      return { discountPercent: true }
    }
    return null
  }

  // todo make as computed?
  get isDiscountPercentInvalid(): boolean {
    return this.voucherForm.controls['discountPercent'].invalid
  }

  getDataToSave() {
    const current = this.voucherForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    const routeId = this.queryVoucher.data()?.id
    if (routeId) patch.id = Number(routeId)

    // Helper to normalize undefined/null to empty string for comparisons
    const norm = (v: any) => (v === undefined || v === null ? '' : v)

    // Required fields for voucher
    if (this.isCreating()) {
      patch.name = current.name
      patch.code = current.code
      patch.discountType = current.discountType
    }

    // Discount fields — include the active one; clear the inactive one if it previously had a value
    if (current.discountType === 'percentage') {
      if (norm(current.discountPercent) !== norm(initial?.discountPercent)) {
        patch.discountPercent = current.discountPercent
      }
      if (current.discountValue) {
        patch.discountValue = null
      }
    } else if (current.discountType === 'value') {
      if (norm(current.discountValue) !== norm(initial?.discountValue)) {
        patch.discountValue = current.discountValue
      }
      if (initial.discountPercent) {
        patch.discountPercent = null
      }
    }

    // Optional fields — include only when changed against initial snapshot
    if (!this.isCreating()) {
      if (norm(current.name) !== norm(initial?.name)) {
        patch.name = current.name
      }
      if (norm(current.code) !== norm(initial?.code)) {
        patch.code = current.code
      }
      if (norm(current.discountType) !== norm(initial?.discountType)) {
        patch.discountType = current.discountType
      }
    }

    if (norm(current?.validFrom) !== norm(initial?.validFrom)) {
      patch.validFrom = current.validFrom
    }
    if (norm(current?.validTo) !== norm(initial?.validTo)) {
      patch.validTo = current.validTo
    }
    if (norm(current?.isPayment) !== norm(initial?.isPayment)) {
      patch.isPayment = current.isPayment
    }
    if (norm(current?.maxUse) !== norm(initial?.maxUse)) {
      patch.maxUse = current.maxUse
    }
    if (this.isCreating() || current.enabled !== initial?.enabled) {
      patch.enabled = current.enabled
    }

    return patch
  }

  // ===== Helpers for cache handling of voucher listings =====
  private isVouchersListingKey(key: unknown[]): boolean {
    if (!Array.isArray(key)) return false
    if (key.length !== 3) return false
    return key[0] === 'vouchers' && typeof key[1] === 'string' && typeof key[2] === 'string'
  }

  private listingContainsId(cached: any, id: number | string): boolean {
    const idStr = String(id)
    if (!cached || !Array.isArray(cached.pages)) return false
    for (const page of cached.pages) {
      const items = Array.isArray(page) ? page : Array.isArray(page?.items) ? page.items : undefined
      if (!Array.isArray(items)) {
        continue
      }
      if (items.some((it: any) => String(it?.id) === idStr)) {
        return true
      }
    }
    return false
  }

  private removeIdFromCachedListing(key: unknown[], id: number | string): void {
    const cached: any = this.queryClient.getQueryData(key as any)
    if (!cached || !Array.isArray(cached.pages)) return
    const idStr = String(id)
    const newPages = cached.pages.map((page: any) => {
      if (Array.isArray(page)) {
        return page.filter((it: any) => String(it?.id) !== idStr)
      }
      if (Array.isArray(page?.items)) {
        return { ...page, items: page.items.filter((it: any) => String(it?.id) !== idStr) }
      }
      return page
    })
    this.queryClient.setQueryData(key as any, { ...cached, pages: newPages })
  }

  // CREATE → refetch all cached voucher listings (['vouchers', sortProp, sortDir])
  private removeVoucherListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['vouchers'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  // DELETE → if present in cached listings, remove it; otherwise do nothing
  private removeFromListingsIfPresent(id: number | string): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['vouchers'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      if (!this.isVouchersListingKey(key as unknown[])) continue
      if (!this.listingContainsId(cached, id)) continue
      this.removeIdFromCachedListing(key as unknown[], id)
    }
  }
}
